/* eslint-disable react/no-unstable-nested-components */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable from '@Components/common/DataTable';
import { FlexRow } from '@Components/common/Layouts';
import Portal from '@Components/common/Layouts/Portal';
import DeleteConfirmationOverlay from '@Components/common/PopupOverlays/DeleteConfirmationOverlay';
import ToolTip from '@Components/RadixComponents/ToolTip';
import { delContact, getContacts } from '@Services/contacts';
import { useTypedSelector } from '@Store/hooks';
import hasErrorBoundary from '@Components/common/hasErrorBoundary';
import { contactsTableColumns } from '@Constants/contacts';

const ContactTable = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const userGroupPermission = useTypedSelector(
    state => state?.common?.userGroupPermission,
  );

  const [openDeleteConfirmation, setOpenDeleteConfirmation] =
    useState<boolean>(false);
  const [contactIDToDelete, setContactIDToDelete] = useState<string>('');

  const searchTerm = useTypedSelector(
    state => state.common.adminSearchState?.contacts,
  );

  const {
    mutate: deleteContact,
    isError,
    error,
    isLoading,
  } = useMutation({
    mutationFn: () => delContact(+contactIDToDelete),
    onSuccess: () => {
      toast.success('Contact deleted successfully');
      setOpenDeleteConfirmation(false);
      queryClient.invalidateQueries({
        queryKey: ['contact-tabledata'],
      });
    },
  });

  const columns = [
    ...contactsTableColumns,
    {
      header: '',
      accessorKey: 'icon',
      cell: ({ row }: any) => {
        const cellId = row?.original?.id;
        return (
          <FlexRow className="naxatw-relative naxatw-mr-6 naxatw-cursor-pointer naxatw-justify-end naxatw-gap-4">
            {userGroupPermission?.includes('Can change contact') ? (
              <ToolTip
                name="edit"
                message="Edit"
                className="naxatw-text-icon-md naxatw-text-matt-100 hover:naxatw-text-calm-teal"
                iconClick={() => navigate(`/data-bank/contact/edit/${cellId}`)}
              />
            ) : null}

            {userGroupPermission?.includes('Can delete contact') ? (
              <ToolTip
                name="delete"
                message="Delete"
                className="naxatw-text-icon-md naxatw-text-matt-100 naxatw-duration-200 hover:naxatw-text-red-500"
                iconClick={() => {
                  setOpenDeleteConfirmation(prev => !prev);
                  setContactIDToDelete(cellId);
                }}
              />
            ) : null}
          </FlexRow>
        );
      },
    },
  ];

  return (
    <div className="naxatw-h-[calc(100%-7rem)] naxatw-w-full lg:naxatw-h-[calc(100%-4.5rem)]">
      <DataTable
        columns={columns}
        queryKey="contact-tabledata"
        queryFn={getContacts}
        isPaginated
        initialState={{
          paginationState: {
            pageIndex: 0,
            pageSize: 25,
          },
        }}
        searchInput={searchTerm || ''}
        sortByKey
      />
      {openDeleteConfirmation && (
        <Portal>
          <DeleteConfirmationOverlay
            onClose={() => setOpenDeleteConfirmation(false)}
            onDelete={() => deleteContact()}
            isError={isError}
            isLoading={isLoading}
            error={error}
          />
        </Portal>
      )}
    </div>
  );
};

export default hasErrorBoundary(ContactTable);
